import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { decodeAndSanitizeHtml } from "./utils/emailAnalysisUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faShield,
  faExclamationTriangle,
  faInfoCircle,
  faEdit,
  faChartBar,
  faPaperPlane,
  faTimes,
  faCheckCircle,
  faShieldAlt,
  faShareAlt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import "./css/EmailManualScanMobile.css";
import "./css/EmailAnalysisSimpleMobile.css";
import "../../css/Common.css";
import HelpGuideButton from "../../HelpGuideButton";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import { useSubscription } from "../../context/SubscriptionContext";
import EmptyStatePanel from "../common/EmptyStatePanel";

// ─────────────────────────────────────────────
// Helper: risk classification for the result
// ─────────────────────────────────────────────
const getResultRiskClass = (result) => {
  if (!result) return "unknown-risk";
  const isScam = result.isScam;
  if (isScam) {
    if (result.confidence >= 0.8) return "very-high-risk";
    if (result.confidence >= 0.6) return "high-risk";
    return "medium-risk";
  }
  return "low-risk";
};

// ─────────────────────────────────────────────
// Accordion Section
// ─────────────────────────────────────────────
const AccordionSection = ({ title, icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`emsm-accordion ${open ? "emsm-accordion--open" : ""}`}>
      <button
        type="button"
        className="emsm-accordion-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="emsm-accordion-title">
          {icon && (
            <FontAwesomeIcon icon={icon} className="emsm-accordion-icon" />
          )}
          {title}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
      </button>
      {open && <div className="emsm-accordion-body">{children}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const EmailManualScanMobile = ({
  emailContent,
  analysisResult,
  error,
  isLoading,
  showConfidenceTooltip,
  setShowConfidenceTooltip,
  handleInputChange,
  handleSubmit,
  handleReset,
  reportId,
  userContext,
  setUserContext,
}) => {
  // Slide state: "form" or "results"
  const [activeSlide, setActiveSlide] = useState("form");
  const share = useShareAlert();
  const { t } = useTranslation();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();
  const containerRef = useRef(null);

  // Navigate to results view
  const showResults = useCallback(() => {
    setActiveSlide("results");
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  // Navigate back to form view
  const showForm = useCallback(() => {
    setActiveSlide("form");
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  // Auto-slide to results when analysis completes
  const prevResultRef = useRef(analysisResult);
  if (analysisResult && analysisResult !== prevResultRef.current) {
    prevResultRef.current = analysisResult;
    if (activeSlide === "form") {
      // Use setTimeout to allow state to settle
      setTimeout(() => setActiveSlide("results"), 100);
    }
  }

  const isScam = analysisResult?.isScam;
  const hasFormContent =
    emailContent.senderEmail || emailContent.subject || emailContent.body;

  return (
    <div className="emsm-container" ref={containerRef}>
      <div
        className={`emsm-view-slider ${
          activeSlide === "results" ? "emsm-slide-results" : ""
        }`}
      >
        {/* ── SLIDE 1: Form Input ── */}
        <div className="emsm-slide emsm-slide-form">
          <div className="emsm-form-wrapper">
            {/* Page Header */}
            <div className="easm-header">
              <div className="easm-header-top">
                <h1 className="main-title">{t("manual.page.title")}</h1>
                <HelpGuideButton guideId={11} />
              </div>
              <p className="common-subtitle">{t("manual.page.subtitle")}</p>
            </div>

            {/* Section Header */}
            <div className="emsm-section-header">
              <FontAwesomeIcon icon={faEdit} className="emsm-section-icon" />
              <span>{t("manual.form.heading")}</span>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="emsm-error-banner">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="emsm-form">
              <div className="emsm-form-field">
                <label htmlFor="emsm-senderEmail">
                  {t("manual.form.senderLabel")}
                </label>
                <input
                  type="text"
                  id="emsm-senderEmail"
                  name="senderEmail"
                  value={emailContent.senderEmail}
                  onChange={handleInputChange}
                  placeholder="example@domain.com"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="emsm-form-field">
                <label htmlFor="emsm-subject">
                  {t("manual.form.subjectLabel")}
                </label>
                <input
                  type="text"
                  id="emsm-subject"
                  name="subject"
                  value={emailContent.subject}
                  onChange={handleInputChange}
                  placeholder="Email subject line"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="emsm-form-field">
                <label htmlFor="emsm-body">{t("manual.form.bodyLabel")}</label>
                <textarea
                  id="emsm-body"
                  name="body"
                  value={emailContent.body}
                  onChange={handleInputChange}
                  placeholder={t("manual.form.bodyPlaceholder")}
                  rows={6}
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="emsm-form-field">
                <label htmlFor="emsm-context">
                  <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
                  {t("manual.form.contextLabel")}
                </label>
                <textarea
                  id="emsm-context"
                  value={userContext}
                  onChange={(e) => setUserContext(e.target.value)}
                  placeholder={t("manual.form.contextPlaceholder")}
                  rows={3}
                  disabled={isLoading}
                />
              </div>

              <div className="btn-group">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {isLoading
                    ? t("manual.form.analyzing")
                    : t("manual.form.analyzeBtn")}
                </button>

                {hasFormContent && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    {t("manual.form.clearBtn")}
                  </button>
                )}
              </div>
            </form>

            {/* Quick-jump to results if available */}
            {analysisResult && (
              <button
                type="button"
                className="btn btn-secondary emsm-view-results-link"
                onClick={showResults}
              >
                <FontAwesomeIcon icon={faChartBar} />
                {t("manual.results.viewResults")}
              </button>
            )}
          </div>
        </div>

        {/* ── SLIDE 2: Analysis Results ── */}
        <div className="emsm-slide emsm-slide-detail">
          <div className="emsm-results-wrapper">
            {/* Back button */}
            <button type="button" className="emsm-back-btn" onClick={showForm}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t("manual.results.backToForm")}
            </button>

            {analysisResult ? (
              <>
                {/* ── Threat Banner (desktop style) ── */}
                <div className="case-details">
                  <div
                    className={`threat-banner emsm-threat-banner ${isScam ? "threat-detected" : analysisResult.confidence <= 0.2 ? "security-failed" : "safe"}`}
                  >
                    <div className="threat-icon">
                      <FontAwesomeIcon
                        icon={isScam ? faExclamationTriangle : faCheckCircle}
                      />
                    </div>
                    <h2 className="threat-title">
                      {isScam
                        ? t("email.detail.scamDetected")
                        : t("manual.results.appearsLegitimate")}
                    </h2>
                    {isScam && (
                      <button
                        className="threat-banner-share-btn"
                        onClick={
                          isIndividualPlan
                            ? undefined
                            : (e) => {
                                e.stopPropagation();
                                share.toggleShare();
                              }
                        }
                        disabled={isIndividualPlan}
                        title={
                          isIndividualPlan
                            ? t("email.detail.shareIndividual")
                            : t("email.detail.shareFamily")
                        }
                      >
                        <FontAwesomeIcon
                          icon={faShareAlt}
                          className="threat-share-icon"
                        />
                        <span className="threat-share-label">
                          {t("email.detail.share")}
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline Share Form */}
                {share.shareOpen && isScam && (
                  <InlineShareForm
                    shareSuccess={share.shareSuccess}
                    shareError={share.shareError}
                    shareMessage={share.shareMessage}
                    setShareMessage={share.setShareMessage}
                    shareLoading={share.shareLoading}
                    onCancel={share.closeShare}
                    onSend={() => share.sendShare(reportId)}
                  />
                )}

                {/* ── Scam Type ── */}
                {analysisResult.scamType &&
                  analysisResult.scamType !== "UNKNOWN" &&
                  analysisResult.scamType !== "None" && (
                    <div className="emsm-scam-type-section">
                      <FontAwesomeIcon
                        icon={faShieldAlt}
                        className="fa-icon-margin-right-8"
                      />
                      <span className="emsm-scam-type-chip">
                        {analysisResult.scamType
                          .toLowerCase()
                          .replace(/_/g, " ")}
                      </span>
                    </div>
                  )}

                {/* ── What Should You Do? (desktop style) ── */}
                {analysisResult.suggestions && (
                  <div className="suggestions-section">
                    <h4 className="subsection-title">
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="fa-icon-margin-right-8"
                      />
                      {t("email.section.whatToDo")}
                    </h4>
                    <SuggestionsBulletList text={analysisResult.suggestions} />
                  </div>
                )}

                {/* ── Explanation (desktop style) ── */}
                <div className="explanation-section">
                  <h4 className="subsection-title">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="fa-icon-margin-right-8"
                    />
                    {t("email.section.explanation")}
                  </h4>
                  <div
                    className="explanation-text"
                    dangerouslySetInnerHTML={{
                      __html: decodeAndSanitizeHtml(
                        analysisResult.explanation || "",
                      ),
                    }}
                  />
                </div>

                {/* ── New Scan button ── */}
                <button
                  type="button"
                  className="btn btn-primary emsm-new-scan-btn"
                  onClick={() => {
                    handleReset();
                    showForm();
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  {t("manual.results.newScan")}
                </button>
              </>
            ) : (
              <div className="emsm-empty-state">
                <EmptyStatePanel
                  icon={faChartBar}
                  title={t("manual.results.noAnalysis")}
                  description={t("manual.results.noAnalysisMobile")}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={showForm}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  {t("manual.results.goToForm")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailManualScanMobile;
